import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getUpcomingEvents } from '../services/events';
import { getCalendarToken } from '../services/users';
import EventCard from '../components/events/EventCard';
import { EventListSkeleton } from '../components/LoadingSkeleton';
import { useAuth } from '../hooks/useAuth';

export default function EventsPage() {
  const [_showCreateForm, _setShowCreateForm] = useState(false);
  const [calendarCopied, setCalendarCopied] = useState(false);
  const { user } = useAuth();

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['events'],
    queryFn: getUpcomingEvents,
  });

  const handleCopyCalendarUrl = async () => {
    try {
      const { calendarToken } = await getCalendarToken();
      const apiBase = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
      const base = apiBase.startsWith('http')
        ? apiBase
        : `${window.location.origin}${apiBase}`;
      const baseWithV1 = base.endsWith('/v1') ? base : `${base}/v1`;
      const url = `${baseWithV1}/events/calendar.ics?calendarToken=${encodeURIComponent(calendarToken)}`;
      await navigator.clipboard.writeText(url);
      setCalendarCopied(true);
      setTimeout(() => setCalendarCopied(false), 3000);
    } catch {
      // fallback: do nothing
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Kommende begivenheder</h1>
        </div>
        <EventListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          Kunne ikke indlæse begivenheder
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Kommende begivenheder</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyCalendarUrl}
            title={calendarCopied ? 'Kopieret!' : 'Kopier kalender-abonnement URL'}
            className="btn"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4em', fontSize: '0.85rem', padding: '0.45em 0.9em' }}
          >
            <svg style={{ width: '1em', height: '1em', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{calendarCopied ? '✓ Kopieret!' : 'Kopiér kalender-URL'}</span>
          </button>
          <Link
            to="/events/create"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Opret begivenhed
          </Link>
        </div>
      </div>
      {calendarCopied && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          Kalender-URL kopieret! Indsæt den i dit kalenderprogram (Google Kalender, Apple Kalender, Outlook osv.) under "Tilføj kalender via URL".
        </div>
      )}

      {/* Events list */}
      {events && events.length > 0 ? (
        <div className="space-y-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} expanded currentUserId={user?.id} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ingen kommende begivenheder
          </h3>
          <p className="text-gray-600 mb-4">
            Opret den første begivenhed for Gribklanen
          </p>
          <Link
            to="/events/create"
            className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Opret begivenhed
          </Link>
        </div>
      )}
    </div>
  );
}
